import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import dayjs from 'dayjs';
import { Subject, Subscription, filter, takeUntil } from 'rxjs';
import { ExecutionDataDownloadQuery, ExtractionDownloadDataType } from '../../models/extraction-reports.model';
import { ExtractionReportDetailRow, ExtractionRequestDetailsItem, ExtractionRequestDetailsQuery } from '../../models/truth-management.model';
import { AuthService } from '../../services/auth.service';
import { ExtractionReportsService } from '../../services/extraction-reports.service';
import { ExtractionRun, ExtractionRunService } from '../../services/extraction-run.service';
import { ModalService, ModalSize, ModalType } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { TruthManagement } from '../../services/truth-management';
import { PageHeaderComponent } from '../page-header/page-header.component';

type TimeFilter = 'today' | 'last7' | 'last30' | 'custom';
const SHARED_DATE_FILTER_KEY = 'docsila.sharedDateFilter.operationsScope';

interface SharedDateFilterState {
  selectedTimeFilter: TimeFilter;
  startDate: string;
  endDate: string;
  startDateDisplay: string;
  endDateDisplay: string;
  updatedAt: number;
}

type SortColumn =
  | 'file_name'
  | 'request_id'
  | 'owner_by'
  | 'page_count'
  | 'start_date_time'
  | 'avg_duration_seconds'
  | 'status';

@Component({
  selector: 'app-extraction-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCalendar, MatNativeDateModule, PageHeaderComponent],
  templateUrl: './extraction-reports.component.html',
  styleUrls: ['./extraction-reports.component.scss'],
})
export class ExtractionReportsComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isRoleResolved = false;
  documentModel = '';
  allRows: ExtractionReportDetailRow[] = [];
  displayedRows: ExtractionReportDetailRow[] = [];

  globalSearch = '';
  selectedTimeFilter: TimeFilter = 'today';
  startDate = '';
  endDate = '';
  startDateDisplay = '';
  endDateDisplay = '';
  endDateInvalid = false;
  dateRangeValidationMessage = '';
  private hasShownInvalidDateRangeToast = false;
  startHour12 = '12';
  startMinute = '00';
  startPeriod: 'AM' | 'PM' = 'AM';
  endHour12 = '12';
  endMinute = '00';
  endPeriod: 'AM' | 'PM' = 'AM';
  readonly hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  readonly minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  activeDateTimePopup: 'start' | 'end' | null = null;
  popupDate: Date | null = null;
  popupHour12 = '12';
  popupMinute = '00';
  popupPeriod: 'AM' | 'PM' = 'AM';
  private popupDirty = false;

  sortColumn: SortColumn | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  isDetailsPopupOpen = false;
  activeDetailsRow: ExtractionReportDetailRow | null = null;
  activeDetailsRowId: string | null = null;
  detailsPopupPosition = { left: 0, top: 0 };

  readonly pageSize = 100;
  currentPage = 1;
  totalPages = 1;
  totalCount = 0;
  hasMore = false;
  isLoading = false;
  pageNumbers: number[] = [];
  isDownloadTypePickerOpen = false;
  downloadTargetRow: ExtractionReportDetailRow | null = null;
  downloadingRequestId: string | null = null;
  readonly downloadTypeOptions: ExtractionDownloadDataType[] = ['logs', 'ocr_pages', 'llm_responses', 'pdfs', 'all'];

  private userTouchedSearch = false;
  private userTouchedSort = false;
  private requestSub?: Subscription;
  private requestEpoch = 0;

  @ViewChild('detailsPopup') detailsPopupRef?: ElementRef<HTMLElement>;
  @ViewChild('tableScrollHost') tableScrollHostRef?: ElementRef<HTMLElement>;

  private readonly destroy$ = new Subject<void>();
  private lastKnownClientId: string | null = null;

  constructor(
    public router: Router,
    public authService: AuthService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private extractionReportsService: ExtractionReportsService,
    private truthManagementService: TruthManagement,
    private extractionRunService: ExtractionRunService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (!this.isSharedDateFilterRoute(event.url)) {
          sessionStorage.removeItem(SHARED_DATE_FILTER_KEY);
        }
      });

    this.authService.userRole$
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        if (role !== null) {
          this.isRoleResolved = true;
          this.cdr.detectChanges();
        }
      });

    const rawModel = this.route.snapshot.paramMap.get('documentModel') || '';
    this.documentModel = decodeURIComponent(rawModel);

    const sharedState = this.loadSharedDateFilterState();
    if (sharedState) {
      this.applySharedDateFilterState(sharedState);
    } else {
      const now = new Date();
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
      this.syncTimePartsFromDateTimes();
      this.syncDateDisplayValues();
      this.saveSharedDateFilterState();
    }

    this.fetchExtractionRequests();

    this.lastKnownClientId = this.authService.getCurrentClient()?.client_id ?? null;
    this.authService.currentClient$.pipe(
      filter(client => client !== null),
      takeUntil(this.destroy$)
    ).subscribe(client => {
      const newClientId = client?.client_id ?? null;
      if (newClientId === this.lastKnownClientId) return;
      this.lastKnownClientId = newClientId;
      this.router.navigate(['/operations-request-analytics']);
    });
  }

  ngOnDestroy(): void {
    this.requestSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  goBack(): void {
    this.router.navigate(['/operations-request-analytics']);
  }

  sortBy(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.userTouchedSort = true;
    this.currentPage = 1;
    this.fetchExtractionRequests();
  }

  onFilterChange(): void {
    const query = this.globalSearch.trim();
    if (query.length === 1) {
      if (this.userTouchedSearch) {
        this.userTouchedSearch = false;
        this.currentPage = 1;
        this.fetchExtractionRequests();
      }
      return;
    }

    this.userTouchedSearch = query.length >= 2;
    this.currentPage = 1;
    this.fetchExtractionRequests();
  }

  onTimeFilterChange(): void {
    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;

    if (this.selectedTimeFilter !== 'custom') {
      this.popupDirty = false;
      this.activeDateTimePopup = null;
    }

    const now = new Date();
    if (this.selectedTimeFilter === 'today') {
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    } else if (this.selectedTimeFilter === 'last7') {
      const start = new Date(now.getTime() - 168 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    } else if (this.selectedTimeFilter === 'last30') {
      const start = new Date(now.getTime() - 720 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    }

    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.saveSharedDateFilterState();

    if (this.selectedTimeFilter !== 'custom') {
      this.currentPage = 1;
      this.fetchExtractionRequests();
    }
  }

  onDateChange(): void {
    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';

    if (this.validateDateRange()) {
      this.saveSharedDateFilterState();
      this.currentPage = 1;
      this.fetchExtractionRequests();
    }
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    this.fetchExtractionRequests();
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  onDateInputChange(field: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.normalizeDisplayDateTimeInput(input.value);

    if (field === 'start') {
      this.startDateDisplay = formatted;
    } else {
      this.endDateDisplay = formatted;
    }

    if (input.value !== formatted) {
      input.value = formatted;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
  }

  onDateInputKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

    if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^[\d/:\s]$/.test(key) && !/^[aApPmM]$/.test(key)) {
      event.preventDefault();
    }
  }

  onDateInputBlur(field: 'start' | 'end'): void {
    const value = (field === 'start' ? this.startDateDisplay : this.endDateDisplay).trim();
    if (!value) {
      this.endDateInvalid = field === 'end';
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    const localDateTime = this.convertDisplayToLocalDateTime(value);
    if (!localDateTime) {
      this.endDateInvalid = field === 'end';
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    const currentValue = field === 'start' ? this.startDate : this.endDate;
    if (field === 'start') {
      this.startDateDisplay = this.formatDateForDisplay(localDateTime);
    } else {
      this.endDateDisplay = this.formatDateForDisplay(localDateTime);
    }

    if (localDateTime === currentValue) {
      return;
    }

    if (field === 'start') {
      this.startDate = localDateTime;
    } else {
      this.endDate = localDateTime;
    }

    this.onDateChange();
  }

  openDateTimePopup(field: 'start' | 'end', event?: Event): void {
    event?.stopPropagation();

    if (this.activeDateTimePopup && this.activeDateTimePopup !== field) {
      this.closeDateTimePopup();
    }

    this.activeDateTimePopup = field;
    this.popupDirty = false;

    const source = field === 'start' ? this.startDate : this.endDate;
    const parsed = this.parseLocalDateTime(source);
    if (parsed) {
      this.popupDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      const isPm = parsed.getHours() >= 12;
      this.popupPeriod = isPm ? 'PM' : 'AM';
      this.popupHour12 = String(parsed.getHours() % 12 || 12).padStart(2, '0');
      this.popupMinute = String(parsed.getMinutes()).padStart(2, '0');
      this.removeCalendarNavTitles();
      return;
    }

    const now = new Date();
    this.popupDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.popupHour12 = '12';
    this.popupMinute = '00';
    this.popupPeriod = 'AM';
    this.removeCalendarNavTitles();
  }

  onPopupPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    this.removeCalendarNavTitles();
  }

  closeDateTimePopup(event?: Event): void {
    event?.stopPropagation();
    if (this.popupDirty) {
      this.onDateChange();
    }
    this.activeDateTimePopup = null;
    this.popupDirty = false;
  }

  onPopupDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    this.popupDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.commitPopupSelection();
  }

  onPopupTimePartChange(): void {
    this.commitPopupSelection();
  }

  getPopupMinDate(field: 'start' | 'end'): Date | null {
    if (field === 'start') {
      return null;
    }

    const start = this.parseLocalDateTime(this.startDate);
    return start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : null;
  }

  getPopupMaxDate(field: 'start' | 'end'): Date | null {
    if (field === 'end') {
      return null;
    }

    return this.getTodayDateForPopup();
  }

  getTodayDateForPopup(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  isPopupOpen(field: 'start' | 'end'): boolean {
    return this.activeDateTimePopup === field;
  }

  getSortIcon(column: SortColumn): SafeHtml {
    if (this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml(
        '<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>&#9650;</span><span>&#9660;</span></div>',
      );
    }

    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '&#9650;' : '&#9660;');
  }

  onDownloadLogs(row: ExtractionReportDetailRow): void {
    if (this.downloadingRequestId !== null) {
      return;
    }

    this.downloadTargetRow = row;
    this.isDownloadTypePickerOpen = true;
    this.cdr.detectChanges();
  }

  onViewResponse(row: ExtractionReportDetailRow): void {
    const requestId = String(row.request_id || '').trim();
    if (!requestId || requestId === '-') {
      this.notificationService.showError('Request ID is not available for this row.');
      return;
    }

    this.extractionReportsService
      .pollExtract(requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const payload = JSON.stringify(response, null, 2);
          this.modalService.show({
            type: ModalType.INFO,
            size: ModalSize.LARGE,
            title: 'View Response',
            compactHeader: true,
            message: `Response details for ${row.file_name}\n\n${payload}`,
            messageAlignLeft: true,
            hideInfoIcon: true,
            showCopyButton: true,
            copyText: payload,
            confirmText: 'Close',
          });
        },
        error: (error) => {
          const errorMessage =
            error?.error?.message ||
            error?.error?.detail ||
            error?.message ||
            'Failed to fetch response details.';

          this.notificationService.showError(errorMessage);
        },
      });
  }

  onViewExtractionData(row: ExtractionReportDetailRow): void {
    const requestId = String(row.request_id || '').trim();
    if (!requestId || requestId === '-') {
      this.notificationService.showError('Request ID is not available for this row.');
      return;
    }

    // Map extraction report row to ExtractionRun interface
    const statusMap: { [key: string]: 'Running' | 'Completed' | 'Failed' | 'Pending' } = {
      'SUCCESS': 'Completed',
      'FAILED': 'Failed',
      'PARTIAL': 'Pending'
    };

    const extractionRun: ExtractionRun = {
      id: requestId,
      fileName: row.file_name,
      pages: row.page_count,
      documentModel: row.document_model,
      ocrMode: '', // Not available in report row
      llmModel: '', // Not available in report row
      status: statusMap[row.status] || 'Pending',
      startedDate: new Date(row.start_date_time),
      completedDate: row.end_date_time ? new Date(row.end_date_time) : null,
      duration: `${row.avg_duration_seconds}s`,
      durationMs: Math.round(row.avg_duration_seconds * 1000),
    };

    // Set the selected run in the service
    this.extractionRunService.setSelectedRun(extractionRun);
    this.extractionRunService.setNavigationSource('extraction-reports');

    // Navigate to the extraction result page
    this.router.navigate(['/data_extraction_result']);
  }

  onSelectDownloadType(dataType: ExtractionDownloadDataType): void {
    const row = this.downloadTargetRow;
    this.closeDownloadTypePicker();

    if (!row) {
      return;
    }

    const days = this.computeDownloadDays(row);
    if (days === null) {
      this.notificationService.showError('Unable to download logs because the row start date is invalid.');
      return;
    }

    if (days > 30) {
      this.notificationService.showError('Select a date range within the last 30 days');
      return;
    }

    this.startExecutionDataDownload(row, dataType, days);
  }

  closeDownloadTypePicker(): void {
    this.isDownloadTypePickerOpen = false;
    this.downloadTargetRow = null;
    this.cdr.detectChanges();
  }

  isRowDownloading(row: ExtractionReportDetailRow): boolean {
    return this.downloadingRequestId !== null && this.downloadingRequestId === row.request_id;
  }

  getDownloadTypeLabel(dataType: ExtractionDownloadDataType): string {
    const labels: Record<ExtractionDownloadDataType, string> = {
      logs: 'Azure Container Job Logs',
      ocr_pages: 'OCR Pages',
      llm_responses: 'LLM Responses',
      pdfs: 'Input and Annotated PDFs',
      all: 'Download All',
    };

    return labels[dataType];
  }

  private computeDownloadDays(row: ExtractionReportDetailRow): number | null {
    const startDate = this.parseApiTimestamp(row.start_date_time);
    if (!startDate) {
      return null;
    }

    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((today.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000));

    return Math.max(1, diffDays + 2);
  }

  private startExecutionDataDownload(row: ExtractionReportDetailRow, dataType: ExtractionDownloadDataType, days: number): void {
    const requestId = String(row.request_id || '').trim();
    if (!requestId || requestId === '-') {
      this.notificationService.showError('Request ID is not available for this row.');
      return;
    }

    const query: ExecutionDataDownloadQuery = {
      request_id: requestId,
      document_model: row.document_model || this.documentModel,
      data_type: dataType,
      days,
    };

    this.downloadingRequestId = requestId;
    this.cdr.detectChanges();

    this.extractionReportsService
      .downloadExecutionData(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const blob = response.body;
          if (!blob || blob.size === 0) {
            this.notificationService.showError('Download failed because the response file is empty.');
            this.downloadingRequestId = null;
            this.cdr.detectChanges();
            return;
          }

          const headerFilename = this.extractFilenameFromContentDisposition(response.headers.get('content-disposition'));
          const fallbackFilename = this.buildExecutionDataFallbackFilename(row, dataType, response.headers.get('content-type'));
          this.triggerBrowserDownload(blob, headerFilename || fallbackFilename);
          const successMessage =
            dataType === 'all'
              ? 'All files downloaded successfully.'
              : `${this.getDownloadTypeLabel(dataType)} downloaded successfully.`;
          this.notificationService.showSuccess(successMessage);

          this.downloadingRequestId = null;
          this.cdr.detectChanges();
        },
        error: (error) => {
          const errorMessage = this.getExecutionDataDownloadErrorMessage(error, dataType);

          this.notificationService.showError(errorMessage);
          this.downloadingRequestId = null;
          this.cdr.detectChanges();
        },
      });
  }

  private getExecutionDataDownloadErrorMessage(error: any, dataType: ExtractionDownloadDataType): string {
    const requestedDataUnavailableMessage = 'Requested Data is not available';

    const statusCode = Number(error?.status);
    const payloadStatus = String(error?.error?.status || '').trim().toUpperCase();
    const payloadMessage = String(error?.error?.message || error?.error?.detail || '').trim();
    const transportMessage = String(error?.message || '').trim();
    const combinedMessage = `${payloadMessage} ${transportMessage}`.toLowerCase();

    const isNoDataError =
      statusCode === 404 ||
      payloadStatus === 'FAILED' ||
      combinedMessage.includes('no data found for the requested type') ||
      combinedMessage.includes('no data found');

    if (isNoDataError) {
      return requestedDataUnavailableMessage;
    }

    if (payloadMessage) {
      return payloadMessage;
    }

    return `Failed to download ${this.getDownloadTypeLabel(dataType)}.`;
  }

  private extractFilenameFromContentDisposition(disposition: string | null): string | null {
    if (!disposition) {
      return null;
    }

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return this.sanitizeFilename(decodeURIComponent(utf8Match[1]));
      } catch {
        return this.sanitizeFilename(utf8Match[1]);
      }
    }

    const basicMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (basicMatch && basicMatch[1]) {
      return this.sanitizeFilename(basicMatch[1]);
    }

    return null;
  }

  private buildExecutionDataFallbackFilename(
    row: ExtractionReportDetailRow,
    dataType: ExtractionDownloadDataType,
    contentType: string | null,
  ): string {
    const safeModel = this.sanitizeFilename((row.document_model || this.documentModel || 'model').replace(/\s+/g, '_'));
    const safeRequest = this.sanitizeFilename(String(row.request_id || 'request').replace(/\s+/g, '_'));
    const now = new Date();
    const timestamp =
      `${now.getFullYear()}` +
      `${String(now.getMonth() + 1).padStart(2, '0')}` +
      `${String(now.getDate()).padStart(2, '0')}` +
      `${String(now.getHours()).padStart(2, '0')}` +
      `${String(now.getMinutes()).padStart(2, '0')}` +
      `${String(now.getSeconds()).padStart(2, '0')}`;
    const extension = this.getFallbackExtensionForDataType(dataType) || this.getExtensionFromContentType(contentType);

    return `${safeModel}_${safeRequest}_${dataType}_${timestamp}${extension}`;
  }

  private getFallbackExtensionForDataType(dataType: ExtractionDownloadDataType): string {
    const extensionMap: Record<ExtractionDownloadDataType, string> = {
      logs: '.xlsx',
      ocr_pages: '.zip',
      llm_responses: '.zip',
      pdfs: '.zip',
      all: '.zip',
    };

    return extensionMap[dataType] || '';
  }

  private getExtensionFromContentType(contentTypeHeader: string | null): string {
    const contentType = String(contentTypeHeader || '').split(';')[0].trim().toLowerCase();

    if (contentType === 'application/zip' || contentType === 'application/x-zip-compressed') {
      return '.zip';
    }

    if (
      contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      contentType === 'application/vnd.ms-excel'
    ) {
      return '.xlsx';
    }

    if (contentType === 'application/pdf') {
      return '.pdf';
    }

    return '.bin';
  }

  private sanitizeFilename(value: string): string {
    const sanitized = String(value || '')
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_');

    return sanitized || 'download';
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(objectUrl);
  }

  onRerun(row: ExtractionReportDetailRow): void {
    this.modalService.show({
      type: ModalType.INFO,
      size: ModalSize.SMALL,
      title: 'Re-run',
      message: `Re-run API integration is pending. Action captured for ${row.file_name}.`,
      confirmText: 'OK',
    });
  }

  openDetailsPopup(row: ExtractionReportDetailRow, event: MouseEvent): void {
    event.stopPropagation();

    if (this.isDetailsPopupOpen && this.activeDetailsRowId === row.request_id) {
      this.closeDetailsPopup();
      return;
    }

    this.activeDetailsRow = row;
    this.activeDetailsRowId = row.request_id;
    this.isDetailsPopupOpen = true;

    const anchor = event.currentTarget as HTMLElement | null;
    if (anchor) {
      this.positionPopupFromAnchor(anchor);
    }

    setTimeout(() => {
      this.repositionPopupForActiveRow();
    });
  }

  closeDetailsPopup(): void {
    this.isDetailsPopupOpen = false;
    this.activeDetailsRow = null;
    this.activeDetailsRowId = null;
  }

  onTableScroll(): void {
    if (!this.isDetailsPopupOpen) {
      return;
    }

    this.repositionPopupForActiveRow();
  }

  displayValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const text = String(value).trim();
    return text.length > 0 ? text : '-';
  }

  displayCost(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return this.displayValue(value);
    }

    const rounded = numeric.toFixed(2);
    if (rounded === '0.00') {
      return '-';
    }

    return `$ ${rounded}`;
  }

  getStatusDisplayLabel(status: ExtractionReportDetailRow['status']): 'Completed' | 'Failed' {
    return status === 'SUCCESS' ? 'Completed' : 'Failed';
  }

  getStatusBadgeClass(status: ExtractionReportDetailRow['status']): string {
    return status === 'SUCCESS' ? 'status-badge status-success' : 'status-badge status-failed';
  }

  formatStartDateHuman(value: unknown): string {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '-';
    }

    const parsedDate = this.parseApiTimestamp(String(value));
    if (!parsedDate) {
      return '-';
    }

    const timestamp = dayjs(parsedDate);
    if (!timestamp.isValid()) {
      return '-';
    }

    const now = dayjs();
    const diffMinutes = now.diff(timestamp, 'minute');

    if (diffMinutes >= 0 && diffMinutes < 60) {
      if (diffMinutes < 1) {
        return 'just now';
      }
      return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
    }

    const diffHours = now.diff(timestamp, 'hour', true);
    if (diffHours >= 0 && diffHours < 24) {
      return timestamp.format('h:mm A');
    }

    const dayDiff = now.startOf('day').diff(timestamp.startOf('day'), 'day');
    if (dayDiff === 1) {
      return `Yesterday ${timestamp.format('h:mm A')}`;
    }

    return timestamp.format('MMM D, YYYY h:mm A');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isDownloadTypePickerOpen) {
      const target = event.target as HTMLElement | null;
      const clickedInsidePicker = Boolean(target?.closest('.download-type-picker'));
      const clickedDownloadButton = Boolean(target?.closest('.btn-download'));

      if (!clickedInsidePicker && !clickedDownloadButton) {
        this.closeDownloadTypePicker();
      }
    }

    if (this.activeDateTimePopup) {
      const path = (event.composedPath?.() || []) as EventTarget[];
      const isInsideDatePopup = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return (
          target.classList.contains('date-time-popup-anchor') ||
          target.classList.contains('date-time-popup-panel') ||
          target.classList.contains('mat-calendar')
        );
      });

      if (!isInsideDatePopup) {
        this.closeDateTimePopup();
      } else {
        return;
      }
    }

    if (!this.isDetailsPopupOpen) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      this.closeDetailsPopup();
      return;
    }

    const popupElement = this.detailsPopupRef?.nativeElement;
    if (popupElement && popupElement.contains(target)) {
      return;
    }

    if (target.closest('.btn-status-eye')) {
      return;
    }

    this.closeDetailsPopup();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isDownloadTypePickerOpen) {
      this.closeDownloadTypePicker();
      return;
    }

    if (this.activeDateTimePopup) {
      this.closeDateTimePopup();
      return;
    }

    this.closeDetailsPopup();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isDetailsPopupOpen) {
      return;
    }

    this.repositionPopupForActiveRow();
  }

  private fetchExtractionRequests(): void {
    const query = this.buildApiQuery();
    const epoch = ++this.requestEpoch;

    this.isLoading = true;
    this.cdr.detectChanges();
    this.requestSub?.unsubscribe();

    this.requestSub = this.truthManagementService
      .getExtractionRequestDetails(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (epoch !== this.requestEpoch) {
            return;
          }

          const data = response?.data;
          const requests = data?.requests || [];
          this.allRows = requests.map((item: ExtractionRequestDetailsItem) => this.mapApiItemToRow(item));
          this.displayedRows = this.allRows;
          this.applyLocalSortFallbackIfNeeded();

          const pagination = data?.pagination;
          this.totalCount = Number(pagination?.total_count || 0);
          this.hasMore = Boolean(pagination?.has_more);
          const effectiveLimit = Number(pagination?.limit || this.pageSize || 100);
          this.totalPages = Math.max(1, Math.ceil(this.totalCount / Math.max(1, effectiveLimit)));
          this.currentPage = Math.min(this.currentPage, this.totalPages);
          this.updatePageNumbers();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          if (epoch !== this.requestEpoch) {
            return;
          }

          this.allRows = [];
          this.displayedRows = [];
          this.totalCount = 0;
          this.totalPages = 1;
          this.currentPage = 1;
          this.hasMore = false;
          this.updatePageNumbers();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private buildApiQuery(): ExtractionRequestDetailsQuery {
    const base: ExtractionRequestDetailsQuery = {
      document_model: this.documentModel,
      start_date: this.toUtcPayloadValue(this.startDate),
      end_date: this.toUtcPayloadValue(this.endDate),
    };

    const search = this.globalSearch.trim();
    if (this.userTouchedSearch && search.length >= 2) {
      base.search = search;
    }

    const sortBy = this.getApiSortBy();
    if (this.userTouchedSort && sortBy) {
      base.sort_by = sortBy;
      base.sort_dir = this.sortDirection;
    }

    if (this.currentPage > 1) {
      base.offset = (this.currentPage - 1) * this.pageSize;
      base.limit = this.pageSize;
    }

    return base;
  }

  getCurrentPageStartRequestIndex(): number {
    if (this.totalCount <= 0 || this.displayedRows.length === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  getCurrentPageEndRequestIndex(): number {
    if (this.totalCount <= 0 || this.displayedRows.length === 0) {
      return 0;
    }

    return this.getCurrentPageStartRequestIndex() + this.displayedRows.length - 1;
  }

  private getApiSortBy(): ExtractionRequestDetailsQuery['sort_by'] | undefined {
    const map: Record<SortColumn, ExtractionRequestDetailsQuery['sort_by'] | undefined> = {
      file_name: 'file_name',
      request_id: undefined,
      owner_by: 'owner',
      page_count: 'page_count',
      start_date_time: 'started_at',
      avg_duration_seconds: undefined,
      status: 'status',
    };

    return this.sortColumn ? map[this.sortColumn] : undefined;
  }

  private applyLocalSortFallbackIfNeeded(): void {
    if (!this.userTouchedSort || !this.sortColumn) {
      return;
    }

    if (!this.isLocalSortFallbackColumn(this.sortColumn)) {
      return;
    }

    const direction = this.sortDirection === 'asc' ? 1 : -1;
    const sorted = [...this.allRows].sort((a, b) => {
      if (this.sortColumn === 'request_id') {
        return this.compareRequestIds(a.request_id, b.request_id) * direction;
      }

      const aDuration = Number(a.avg_duration_seconds || 0);
      const bDuration = Number(b.avg_duration_seconds || 0);
      return (aDuration - bDuration) * direction;
    });

    this.allRows = sorted;
    this.displayedRows = sorted;
  }

  private isLocalSortFallbackColumn(column: SortColumn): boolean {
    return column === 'request_id' || column === 'avg_duration_seconds';
  }

  private compareRequestIds(a: string, b: string): number {
    const valueA = String(a || '').trim();
    const valueB = String(b || '').trim();

    if (valueA === valueB) {
      return 0;
    }

    if (!valueA || valueA === '-') {
      return 1;
    }

    if (!valueB || valueB === '-') {
      return -1;
    }

    return valueA.localeCompare(valueB, undefined, { sensitivity: 'base' });
  }

  private mapApiItemToRow(item: ExtractionRequestDetailsItem): ExtractionReportDetailRow {
    const startedAt = typeof item.started_at === 'string' ? item.started_at : '';
    const completedAt = typeof item.completed_at === 'string' ? item.completed_at : '';

    return {
      document_model: (item.document_model as string) || this.documentModel,
      file_name: (item.file_name as string) || '-',
      request_id: String(item.request_id || '-'),
      client_request_id: String(item.client_request_id || '-'),
      owner_by: (item.owner_by as string) || (item.owner as string) || '-',
      job_id: String(item.job_id || '-'),
      page_count: Number(item.page_count || 0),
      llm_cost: Number(item.llm_cost || 0),
      ocr_cost: Number(item.ocr_cost || 0),
      start_date_time: startedAt,
      end_date_time: completedAt,
      avg_duration_seconds: this.resolveDurationSeconds(item, startedAt, completedAt),
      message: (item.message as string) || '',
      status: this.normalizeStatus(item.status),
    };
  }

  private resolveDurationSeconds(item: ExtractionRequestDetailsItem, startedAt: string, completedAt: string): number {
    if (item.avg_duration_seconds !== undefined && item.avg_duration_seconds !== null) {
      return Number(item.avg_duration_seconds) || 0;
    }

    if (item.duration_seconds !== undefined && item.duration_seconds !== null) {
      return Number(item.duration_seconds) || 0;
    }

    const startDate = startedAt ? this.parseApiTimestamp(startedAt) : null;
    const endDate = completedAt ? this.parseApiTimestamp(completedAt) : null;
    const start = startDate ? startDate.getTime() : NaN;
    const end = endDate ? endDate.getTime() : NaN;
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return (end - start) / 1000;
    }

    return 0;
  }

  private normalizeStatus(status: unknown): 'SUCCESS' | 'FAILED' {
    const text = String(status || '').toUpperCase();
    if (text === 'SUCCESS') {
      return 'SUCCESS';
    }

    if (text === 'FAILED' || text === 'PARTIAL') {
      return 'FAILED';
    }

    if (text.includes('FAIL') || text.includes('PART')) {
      return 'FAILED';
    }

    return 'SUCCESS';
  }

  private parseApiTimestamp(rawValue: string): Date | null {
    const value = String(rawValue || '').trim();
    if (!value) {
      return null;
    }

    const hasTimezone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const normalized = hasTimezone ? value : `${value}Z`;
    const parsed = new Date(normalized);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toUtcPayloadValue(localDateTime: string): string {
    const parsed = this.parseLocalDateTime(localDateTime);
    if (parsed) {
      return parsed.toISOString();
    }

    return localDateTime;
  }

  private updatePageNumbers(): void {
    const pages: number[] = [];
    const maxVisible = 5;
    const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);
    const adjustedStart = Math.max(1, end - maxVisible + 1);

    for (let i = adjustedStart; i <= end; i++) {
      pages.push(i);
    }

    this.pageNumbers = pages;
  }

  private commitPopupSelection(): void {
    if (!this.activeDateTimePopup || !this.popupDate) {
      return;
    }

    const datePart = this.formatDatePartFromDate(this.popupDate);
    const localDateTime = this.buildLocalDateTime(datePart, this.popupHour12, this.popupMinute, this.popupPeriod);
    const currentValue = this.activeDateTimePopup === 'start' ? this.startDate : this.endDate;

    if (localDateTime === currentValue) {
      this.syncDateDisplayValues();
      this.updateEndDateInvalidState();
      return;
    }

    if (this.activeDateTimePopup === 'start') {
      this.startDate = localDateTime;
      this.startHour12 = this.popupHour12;
      this.startMinute = this.popupMinute;
      this.startPeriod = this.popupPeriod;
    } else {
      this.endDate = localDateTime;
      this.endHour12 = this.popupHour12;
      this.endMinute = this.popupMinute;
      this.endPeriod = this.popupPeriod;
    }

    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';
    this.popupDirty = true;
    this.updateEndDateInvalidState();
  }

  private removeCalendarNavTitles(): void {
    setTimeout(() => {
      const navButtons = document.querySelectorAll(
        '.date-time-popup-panel .mat-calendar-previous-button, .date-time-popup-panel .mat-calendar-next-button',
      );

      navButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
          button.removeAttribute('title');
        }
      });
    }, 0);
  }

  private formatDateTimeForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private buildLocalDateTime(datePart: string, hour12: string, minute: string, period: 'AM' | 'PM'): string {
    const monthDayYear = this.convertDisplayToLocalDateTime(`${this.toDisplayDate(datePart)} ${hour12}:${minute} ${period}`);
    if (monthDayYear) {
      return monthDayYear;
    }

    const hour24 = period === 'PM' ? (Number(hour12) % 12) + 12 : Number(hour12) % 12;
    return `${datePart}T${String(hour24).padStart(2, '0')}:${minute}`;
  }

  private toDisplayDate(datePart: string): string {
    const parts = datePart.split('-');
    if (parts.length !== 3) {
      return datePart;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  private formatDatePartFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseLocalDateTime(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private formatDateTimeForDisplay(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    const minutes = String(date.getMinutes()).padStart(2, '0');
    const isPm = date.getHours() >= 12;
    const hour12 = date.getHours() % 12 || 12;
    const period = isPm ? 'PM' : 'AM';

    return `${month}/${day}/${year} ${String(hour12).padStart(2, '0')}:${minutes} ${period}`;
  }

  private syncDateDisplayValues(): void {
    this.startDateDisplay = this.formatDateForDisplay(this.startDate);
    this.endDateDisplay = this.formatDateForDisplay(this.endDate);
  }

  private syncTimePartsFromDateTimes(): void {
    const start = this.parseLocalDateTime(this.startDate);
    if (start) {
      const startIsPm = start.getHours() >= 12;
      this.startPeriod = startIsPm ? 'PM' : 'AM';
      this.startHour12 = String(start.getHours() % 12 || 12).padStart(2, '0');
      this.startMinute = String(start.getMinutes()).padStart(2, '0');
    }

    const end = this.parseLocalDateTime(this.endDate);
    if (end) {
      const endIsPm = end.getHours() >= 12;
      this.endPeriod = endIsPm ? 'PM' : 'AM';
      this.endHour12 = String(end.getHours() % 12 || 12).padStart(2, '0');
      this.endMinute = String(end.getMinutes()).padStart(2, '0');
    }
  }

  private normalizeDisplayDateTimeInput(value: string): string {
    return value.replace(/\s+/g, ' ').toUpperCase();
  }

  private loadSharedDateFilterState(): SharedDateFilterState | null {
    const raw = sessionStorage.getItem(SHARED_DATE_FILTER_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<SharedDateFilterState>;
      const selected = parsed.selectedTimeFilter;
      if (!selected || !['today', 'last7', 'last30', 'custom'].includes(selected)) {
        return null;
      }

      const start = typeof parsed.startDate === 'string' ? parsed.startDate : '';
      const end = typeof parsed.endDate === 'string' ? parsed.endDate : '';
      if (!this.parseLocalDateTime(start) || !this.parseLocalDateTime(end)) {
        return null;
      }

      return {
        selectedTimeFilter: selected as TimeFilter,
        startDate: start,
        endDate: end,
        startDateDisplay: typeof parsed.startDateDisplay === 'string' ? parsed.startDateDisplay : this.formatDateForDisplay(start),
        endDateDisplay: typeof parsed.endDateDisplay === 'string' ? parsed.endDateDisplay : this.formatDateForDisplay(end),
        updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      };
    } catch {
      return null;
    }
  }

  private applySharedDateFilterState(state: SharedDateFilterState): void {
    this.selectedTimeFilter = state.selectedTimeFilter;
    this.startDate = state.startDate;
    this.endDate = state.endDate;
    this.startDateDisplay = state.startDateDisplay || this.formatDateForDisplay(state.startDate);
    this.endDateDisplay = state.endDateDisplay || this.formatDateForDisplay(state.endDate);
    this.syncTimePartsFromDateTimes();
    this.updateEndDateInvalidState();
  }

  private saveSharedDateFilterState(): void {
    const payload: SharedDateFilterState = {
      selectedTimeFilter: this.selectedTimeFilter,
      startDate: this.startDate,
      endDate: this.endDate,
      startDateDisplay: this.startDateDisplay,
      endDateDisplay: this.endDateDisplay,
      updatedAt: Date.now(),
    };

    sessionStorage.setItem(SHARED_DATE_FILTER_KEY, JSON.stringify(payload));
  }

  private isSharedDateFilterRoute(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return (
      path === '/indexing' ||
      path.startsWith('/operations-request-analytics') ||
      path === '/extraction-reports' ||
      path.startsWith('/extraction-reports/') ||
      path === '/source'
    );
  }

  private convertDisplayToLocalDateTime(value: string): string | null {
    const trimmed = value.trim().toUpperCase();
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/);
    if (!match) {
      return null;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);

    if (month < 1 || month > 12 || year < 1900 || year > 2100) {
      return null;
    }

    let hours = 0;
    let minutes = 0;
    if (match[4] !== undefined && match[5] !== undefined && match[6] !== undefined) {
      const hour12 = Number(match[4]);
      minutes = Number(match[5]);
      const period = match[6];

      if (hour12 < 1 || hour12 > 12 || minutes < 0 || minutes > 59) {
        return null;
      }

      hours = period === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
    }

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return this.formatDateTimeForInput(date);
  }

  private formatDateForDisplay(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const date = this.parseLocalDateTime(dateValue);
    if (!date) {
      return '';
    }

    return this.formatDateTimeForDisplay(date);
  }

  private updateEndDateInvalidState(): void {
    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    const now = new Date();

    if (!start || !end) {
      this.endDateInvalid = false;
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      if (!this.hasShownInvalidDateRangeToast) {
        this.notificationService.showError('End date/time must be greater than start date/time.');
        this.hasShownInvalidDateRangeToast = true;
      }
      return;
    }

    this.endDateInvalid = end.getTime() > now.getTime();
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
  }

  private validateDateRange(): boolean {
    if (!this.startDate || !this.endDate) {
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return false;
    }

    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    if (!start || !end) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return false;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      if (!this.hasShownInvalidDateRangeToast) {
        this.notificationService.showError('End date/time must be greater than start date/time.');
        this.hasShownInvalidDateRangeToast = true;
      }
      return false;
    }

    if (end.getTime() > Date.now()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return false;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
    return true;
  }

  private repositionPopupForActiveRow(): void {
    if (!this.activeDetailsRowId) {
      this.closeDetailsPopup();
      return;
    }

    const anchor = this.getEyeButtonForRow(this.activeDetailsRowId);
    if (!anchor || !this.isAnchorVisibleInTableViewport(anchor)) {
      this.closeDetailsPopup();
      return;
    }

    this.positionPopupFromAnchor(anchor);
  }

  private getEyeButtonForRow(rowId: string): HTMLElement | null {
    const wrapper = this.tableScrollHostRef?.nativeElement;
    if (!wrapper) {
      return null;
    }

    const candidates = wrapper.querySelectorAll<HTMLElement>('.btn-status-eye[data-eye-row-id]');
    return Array.from(candidates).find((button) => button.dataset['eyeRowId'] === rowId) ?? null;
  }

  private isAnchorVisibleInTableViewport(anchor: HTMLElement): boolean {
    const wrapper = this.tableScrollHostRef?.nativeElement;
    if (!wrapper) {
      return true;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    return anchorRect.bottom > wrapperRect.top && anchorRect.top < wrapperRect.bottom;
  }

  private positionPopupFromAnchor(anchor: HTMLElement): void {
    const anchorRect = anchor.getBoundingClientRect();
    const popupElement = this.detailsPopupRef?.nativeElement;
    const popupWidth = popupElement?.offsetWidth ?? 288;
    const popupHeight = popupElement?.offsetHeight ?? 206;
    const gap = window.innerWidth <= 640 ? 6 : 8;

    let left = anchorRect.right + gap;

    if (left + popupWidth > window.innerWidth - gap) {
      left = anchorRect.left - popupWidth - gap;
    }

    if (left < gap) {
      left = gap;
    }

    const spaceBelow = window.innerHeight - anchorRect.bottom - gap;
    const spaceAbove = anchorRect.top - gap;

    let top = anchorRect.bottom + gap;
    if (spaceBelow < popupHeight && spaceAbove >= popupHeight) {
      top = anchorRect.top - popupHeight - gap;
    }

    if (top + popupHeight > window.innerHeight - gap) {
      top = window.innerHeight - popupHeight - gap;
    }

    if (top < gap) {
      top = gap;
    }

    this.detailsPopupPosition = { left, top };
  }

  private getMockRows(documentModel: string): ExtractionReportDetailRow[] {
    const model = documentModel || 'UNKNOWN_MODEL';

    return [
      {
        document_model: model,
        file_name: `${model.toLowerCase().replace(/\s+/g, '_')}_001.pdf`,
        request_id: 'REQ-1001',
        owner_by: 'nitish.s',
        job_id: 'JOB-501',
        page_count: 8,
        llm_cost: 0.23,
        ocr_cost: 0.03,
        start_date_time: '2026-07-15T10:20:00',
        end_date_time: '2026-07-15T10:20:18',
        avg_duration_seconds: 18.0,
        message: 'Extraction completed successfully',
        status: 'SUCCESS',
      },
      {
        document_model: model,
        file_name: `${model.toLowerCase().replace(/\s+/g, '_')}_002.pdf`,
        request_id: 'REQ-1002',
        owner_by: 'anita.k',
        job_id: 'JOB-502',
        page_count: 12,
        llm_cost: 0.31,
        ocr_cost: 0.04,
        start_date_time: '2026-07-15T11:05:00',
        end_date_time: '2026-07-15T11:05:26',
        avg_duration_seconds: 26.0,
        message: 'Partial extraction, one field missing',
        status: 'PARTIAL',
      },
      {
        document_model: model,
        file_name: `${model.toLowerCase().replace(/\s+/g, '_')}_003.pdf`,
        request_id: 'REQ-1003',
        owner_by: 'rahul.m',
        job_id: 'JOB-503',
        page_count: 5,
        llm_cost: 0.14,
        ocr_cost: 0.02,
        start_date_time: '2026-07-15T12:10:00',
        end_date_time: '2026-07-15T12:10:16',
        avg_duration_seconds: 16.0,
        message: 'OCR timeout on final page',
        status: 'FAILED',
      },
    ];
  }
}
